import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../store/authStore'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { cn, formatDate } from '../lib/utils'
import {
  useTelegramSubscriptions,
  useTelegramStats,
  useBotStatus,
  useApproveSubscription,
  useRejectSubscription,
  useDeleteSubscription,
  type TelegramSubscription,
} from '../api/telegram'
import { useStages, useReorderStages } from '../api/projects'
import { useUpdateProfile, useChangePassword } from '../api/users'
import { useNotificationPreferences, useUpdateNotificationPreferences, useAiStatus, useUpdateAiModel } from '../api/settings'
import { useFirefliesStatus, useConnectFireflies, useDisconnectFireflies } from '../api/integrations'
import { apiClient } from '../api/client'

type Tab = 'profile' | 'telegram' | 'notifications' | 'stages' | 'ai' | 'fireflies'

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('profile')
  const [subscriptionFilter, setSubscriptionFilter] = useState<string>('')
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)

  const tabs: { id: Tab; label: string; icon: React.ReactNode; badge?: number }[] = [
    {
      id: 'profile',
      label: 'Профиль',
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      ),
    },
    {
      id: 'telegram',
      label: 'Telegram',
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" />
        </svg>
      ),
    },
    {
      id: 'notifications',
      label: 'Уведомления',
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      ),
    },
    {
      id: 'stages',
      label: 'Стадии проектов',
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M9 3v18" />
          <path d="M15 3v18" />
        </svg>
      ),
    },
    {
      id: 'ai',
      label: 'AI',
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 3l1.912 5.813a2 2 0 001.275 1.275L21 12l-5.813 1.912a2 2 0 00-1.275 1.275L12 21l-1.912-5.813a2 2 0 00-1.275-1.275L3 12l5.813-1.912a2 2 0 001.275-1.275L12 3z" />
        </svg>
      ),
    },
    {
      id: 'fireflies',
      label: 'Fireflies',
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="23" />
          <line x1="8" y1="23" x2="16" y2="23" />
        </svg>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold tracking-tight text-white animate-in">Настройки</h1>

      <div className="flex gap-8">
        {/* Sidebar */}
        <div className="w-64 flex-shrink-0">
          <nav className="glass-card rounded-xl p-2 space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors',
                  activeTab === tab.id
                    ? 'bg-white/10 text-white font-medium'
                    : 'text-text-secondary hover:text-white hover:bg-white/5'
                )}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 max-w-3xl">
          {activeTab === 'profile' && (
            <ProfileTab user={user} logout={logout} />
          )}

          {activeTab === 'telegram' && (
            <TelegramTab
              filter={subscriptionFilter}
              setFilter={setSubscriptionFilter}
            />
          )}

          {activeTab === 'notifications' && (
            <NotificationsTab user={user} />
          )}

          {activeTab === 'stages' && (
            <StagesTab />
          )}

          {activeTab === 'ai' && (
            <AiTab />
          )}

          {activeTab === 'fireflies' && (
            <FirefliesTab />
          )}
        </div>
      </div>
    </div>
  )
}

// Profile Tab Component
function ProfileTab({ user, logout }: { user: any; logout: () => void }) {
  const [isEditing, setIsEditing] = useState(false)
  const [editUsername, setEditUsername] = useState(user?.username || '')
  const [editEmail, setEditEmail] = useState(user?.email || '')
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [passwordMsg, setPasswordMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const updateProfile = useUpdateProfile()
  const changePassword = useChangePassword()
  const updateUser = useAuthStore((s) => s.updateUser)

  const handleSaveProfile = async () => {
    const data: Record<string, string> = {}
    if (editUsername !== user?.username) data.username = editUsername
    if (editEmail !== user?.email) data.email = editEmail

    if (Object.keys(data).length === 0) {
      setIsEditing(false)
      return
    }

    updateProfile.mutate(data, {
      onSuccess: (result) => {
        updateUser({ username: result.username, email: result.email })
        setIsEditing(false)
      },
    })
  }

  const handleChangePassword = async () => {
    setPasswordMsg(null)
    changePassword.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          setPasswordMsg({ type: 'success', text: 'Пароль успешно изменён' })
          setCurrentPassword('')
          setNewPassword('')
          setShowPasswordForm(false)
        },
        onError: (err: any) => {
          setPasswordMsg({ type: 'error', text: err.message || 'Ошибка смены пароля' })
        },
      }
    )
  }

  return (
    <div className="space-y-6">
      <Card className="animate-in">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Профиль</CardTitle>
          {!isEditing && (
            <Button variant="secondary" onClick={() => {
              setEditUsername(user?.username || '')
              setEditEmail(user?.email || '')
              setIsEditing(true)
            }}>
              Редактировать
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-white/20 to-white/5 border border-white/10 flex items-center justify-center text-2xl font-bold text-white">
              {user?.username?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">{user?.username}</h3>
              <p className="text-sm text-text-tertiary">{user?.email}</p>
              <p className="text-xs text-text-tertiary mt-1">Роль: {user?.role}</p>
            </div>
          </div>

          {isEditing ? (
            <div className="pt-4 border-t border-white/10 space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1.5">Username</label>
                <input
                  value={editUsername}
                  onChange={(e) => setEditUsername(e.target.value)}
                  className="w-full px-4 py-3 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary focus:outline-none focus:border-accent-lime/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1.5">Email</label>
                <input
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary focus:outline-none focus:border-accent-lime/50"
                />
              </div>
              <div className="flex gap-3">
                <Button variant="secondary" onClick={() => setIsEditing(false)} disabled={updateProfile.isPending}>
                  Отмена
                </Button>
                <Button variant="primary" onClick={handleSaveProfile} loading={updateProfile.isPending}>
                  Сохранить
                </Button>
              </div>
            </div>
          ) : (
            <div className="pt-4 border-t border-white/10">
              <h4 className="text-sm font-medium text-text-secondary mb-3">Информация аккаунта</h4>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-text-tertiary">Username</dt>
                  <dd className="font-medium text-white">{user?.username}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-text-tertiary">Email</dt>
                  <dd className="font-medium text-white">{user?.email}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-text-tertiary">Telegram</dt>
                  <dd className="font-medium text-white">
                    {user?.telegramUsername ? `@${user.telegramUsername}` : 'Не привязан'}
                  </dd>
                </div>
              </dl>
            </div>
          )}

          {/* Password Change */}
          <div className="pt-4 border-t border-white/10">
            {passwordMsg && (
              <div className={cn(
                'p-3 rounded-lg text-sm mb-4',
                passwordMsg.type === 'success'
                  ? 'bg-accent-green/10 border border-accent-green/20 text-accent-green'
                  : 'bg-red-500/10 border border-red-500/20 text-red-400'
              )}>
                {passwordMsg.text}
              </div>
            )}
            {showPasswordForm ? (
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-text-secondary">Смена пароля</h4>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1.5">Текущий пароль</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary focus:outline-none focus:border-accent-lime/50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1.5">Новый пароль</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary focus:outline-none focus:border-accent-lime/50"
                    placeholder="Минимум 6 символов"
                  />
                </div>
                <div className="flex gap-3">
                  <Button variant="secondary" onClick={() => { setShowPasswordForm(false); setCurrentPassword(''); setNewPassword('') }}>
                    Отмена
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleChangePassword}
                    loading={changePassword.isPending}
                    disabled={!currentPassword || newPassword.length < 6}
                  >
                    Сменить пароль
                  </Button>
                </div>
              </div>
            ) : (
              <Button variant="secondary" onClick={() => setShowPasswordForm(true)}>
                Сменить пароль
              </Button>
            )}
          </div>

          <div className="pt-4 border-t border-white/10">
            <Button variant="danger" onClick={logout}>
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Выйти из аккаунта
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// Telegram Tab Component
function TelegramTab({ filter, setFilter }: { filter: string; setFilter: (f: string) => void }) {
  const { data: stats, isLoading: statsLoading } = useTelegramStats()
  const { data: botStatus } = useBotStatus()
  const { data: subscriptions, isLoading } = useTelegramSubscriptions({
    status: filter || undefined,
    pageSize: 50,
  })

  const approveSubscription = useApproveSubscription()
  const rejectSubscription = useRejectSubscription()
  const deleteSubscription = useDeleteSubscription()

  const handleApprove = (id: string) => {
    approveSubscription.mutate(id)
  }

  const handleReject = (id: string) => {
    rejectSubscription.mutate(id)
  }

  const handleDelete = (id: string) => {
    if (confirm('Удалить подписку? Пользователь сможет подписаться снова.')) {
      deleteSubscription.mutate(id)
    }
  }

  return (
    <div className="space-y-6">
      {/* Bot Status Card */}
      <Card className="animate-in">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" />
            </svg>
            Telegram Bot
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 mb-4">
            <div className={cn(
              'w-3 h-3 rounded-full',
              botStatus?.running ? 'status-dot' : 'bg-red-500'
            )} />
            <span className="text-sm text-white">
              {botStatus?.running ? 'Бот запущен' : 'Бот не запущен'}
            </span>
          </div>

          <div className="p-4 bg-white/5 border border-white/10 rounded-lg">
            <p className="text-sm text-text-secondary mb-2">
              Для получения уведомлений пользователи должны:
            </p>
            <ol className="text-sm text-text-tertiary space-y-1 list-decimal list-inside">
              <li>Найти бота <code className="px-1.5 py-0.5 bg-white/10 rounded text-white">@eleventhirtystudiobot</code></li>
              <li>Нажать /start</li>
              <li>Дождаться одобрения администратором</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      {/* Stats Card */}
      <Card className="animate-in delay-1">
        <CardHeader>
          <CardTitle>Статистика подписок</CardTitle>
        </CardHeader>
        <CardContent>
          {statsLoading ? (
            <div className="text-text-tertiary">Загрузка...</div>
          ) : (
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center p-3 bg-white/5 border border-white/10 rounded-lg">
                <div className="text-2xl font-bold text-white">{stats?.total || 0}</div>
                <div className="text-xs text-text-tertiary">Всего</div>
              </div>
              <div className="text-center p-3 bg-accent-orange/10 border border-accent-orange/20 rounded-lg">
                <div className="text-2xl font-bold text-accent-orange">{stats?.pending || 0}</div>
                <div className="text-xs text-text-tertiary">Ожидают</div>
              </div>
              <div className="text-center p-3 bg-accent-green/10 border border-accent-green/20 rounded-lg">
                <div className="text-2xl font-bold text-accent-green">{stats?.approved || 0}</div>
                <div className="text-xs text-text-tertiary">Одобрено</div>
              </div>
              <div className="text-center p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <div className="text-2xl font-bold text-red-400">{stats?.rejected || 0}</div>
                <div className="text-xs text-text-tertiary">Отклонено</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Subscriptions List */}
      <Card className="animate-in delay-2">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Подписки на уведомления</CardTitle>
          <div className="flex gap-2">
            {['', 'pending', 'approved', 'rejected'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={cn(
                  'px-3 py-1.5 text-xs rounded-lg transition-colors border',
                  filter === status
                    ? 'bg-white text-black border-white'
                    : 'bg-white/5 text-text-secondary border-white/10 hover:text-white hover:border-white/20'
                )}
              >
                {status === '' ? 'Все' : status === 'pending' ? 'Ожидают' : status === 'approved' ? 'Одобрено' : 'Отклонено'}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-text-tertiary">Загрузка...</div>
          ) : !subscriptions?.items.length ? (
            <div className="text-center py-8 text-text-tertiary">
              {filter ? 'Нет подписок с таким статусом' : 'Нет подписок'}
            </div>
          ) : (
            <div className="space-y-2">
              {subscriptions.items.map((sub) => (
                <SubscriptionRow
                  key={sub.id}
                  subscription={sub}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// Subscription Row Component
function SubscriptionRow({
  subscription,
  onApprove,
  onReject,
  onDelete,
}: {
  subscription: TelegramSubscription
  onApprove: (id: string) => void
  onReject: (id: string) => void
  onDelete: (id: string) => void
}) {
  const statusColors = {
    pending: 'bg-accent-orange/20 text-accent-orange border-accent-orange/30',
    approved: 'bg-accent-green/20 text-accent-green border-accent-green/30',
    rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
  }

  const statusLabels = {
    pending: 'Ожидает',
    approved: 'Одобрен',
    rejected: 'Отклонён',
  }

  const displayName = subscription.firstName
    ? `${subscription.firstName}${subscription.lastName ? ` ${subscription.lastName}` : ''}`
    : subscription.telegramUsername || subscription.telegramId

  return (
    <div className="flex items-center gap-4 p-3 bg-white/5 border border-white/10 rounded-lg">
      <div className="w-10 h-10 rounded-full bg-accent-blue/20 border border-accent-blue/30 flex items-center justify-center text-accent-blue font-bold">
        {(subscription.firstName?.[0] || subscription.telegramUsername?.[0] || 'T').toUpperCase()}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-white truncate">{displayName}</span>
          {subscription.telegramUsername && (
            <span className="text-xs text-text-tertiary">@{subscription.telegramUsername}</span>
          )}
        </div>
        <div className="text-xs text-text-tertiary">
          ID: {subscription.telegramId} · {formatDate(subscription.createdAt)}
        </div>
      </div>

      <span className={cn('px-2.5 py-1 text-xs rounded-full border', statusColors[subscription.status])}>
        {statusLabels[subscription.status]}
      </span>

      <div className="flex gap-1">
        {subscription.status !== 'approved' && (
          <button
            onClick={() => onApprove(subscription.id)}
            className="p-2 text-accent-green hover:bg-accent-green/10 rounded-lg transition-colors"
            title="Одобрить"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </button>
        )}
        {subscription.status !== 'rejected' && (
          <button
            onClick={() => onReject(subscription.id)}
            className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
            title="Отклонить"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
        <button
          onClick={() => onDelete(subscription.id)}
          className="p-2 text-text-tertiary hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
          title="Удалить"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
        </button>
      </div>
    </div>
  )
}

// Telegram Link Form Component
function TelegramLinkForm() {
  const [username, setUsername] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const queryClient = useQueryClient()

  const linkTelegram = useMutation({
    mutationFn: (telegramUsername: string) =>
      apiClient('/auth/telegram-link', {
        method: 'POST',
        body: JSON.stringify({ telegramUsername }),
      }),
    onSuccess: () => {
      setSuccess(true)
      setError('')
      queryClient.invalidateQueries({ queryKey: ['auth'] })
    },
    onError: (err: Error) => {
      setError(err.message)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!username.trim()) return
    linkTelegram.mutate(username)
  }

  if (success) {
    return (
      <div className="p-4 bg-accent-green/10 border border-accent-green/20 rounded-lg">
        <p className="text-sm font-medium text-accent-green">Telegram аккаунт успешно привязан!</p>
        <p className="text-xs text-text-tertiary mt-1">Обновите страницу для отображения.</p>
      </div>
    )
  }

  return (
    <div className="p-4 bg-white/5 border border-white/10 rounded-lg">
      <p className="text-sm text-text-secondary mb-3">
        Привяжите Telegram аккаунт для получения уведомлений. Сначала напишите боту @eleventhirtystudiobot команду /start.
      </p>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="@username"
          className="flex-1 px-3 py-2 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary text-sm placeholder-text-tertiary focus:outline-none focus:border-accent-blue/50"
        />
        <Button
          type="submit"
          variant="secondary"
          disabled={linkTelegram.isPending || !username.trim()}
        >
          {linkTelegram.isPending ? 'Привязка...' : 'Привязать'}
        </Button>
      </form>
      {error && (
        <p className="text-xs text-red-400 mt-2">{error}</p>
      )}
    </div>
  )
}

// Notifications Tab Component
function NotificationsTab({ user }: { user: any }) {
  const { data: prefs, isLoading } = useNotificationPreferences()
  const updatePrefs = useUpdateNotificationPreferences()

  const toggles = [
    { key: 'notifyNewLeads' as const, label: 'Новые заявки' },
    { key: 'notifyProjectStatus' as const, label: 'Изменения статусов проектов' },
    { key: 'notifyDeadlines' as const, label: 'Напоминания о дедлайнах' },
    { key: 'notifyPayments' as const, label: 'Платежи' },
  ]

  const handleToggle = (key: string, value: boolean) => {
    updatePrefs.mutate({ [key]: value })
  }

  return (
    <Card className="animate-in">
      <CardHeader>
        <CardTitle>Уведомления</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <h4 className="text-sm font-medium text-text-secondary mb-3">Telegram уведомления</h4>

          {user?.telegramId ? (
            <div className="p-4 bg-accent-green/10 border border-accent-green/20 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-accent-green/20 flex items-center justify-center">
                  <svg className="w-5 h-5 text-accent-green" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-accent-green">Telegram привязан</p>
                  <p className="text-sm text-text-tertiary">
                    {user.telegramUsername ? `@${user.telegramUsername}` : `ID: ${user.telegramId}`}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <TelegramLinkForm />
          )}
        </div>

        <div className="pt-4 border-t border-white/10">
          <h4 className="text-sm font-medium text-text-secondary mb-3">Настройки уведомлений</h4>
          {isLoading ? (
            <div className="text-center py-4 text-text-tertiary">Загрузка...</div>
          ) : (
            <div className="space-y-2">
              {toggles.map(({ key, label }) => (
                <label key={key} className="flex items-center justify-between p-3 bg-white/5 border border-white/10 rounded-lg cursor-pointer hover:bg-white/8 transition-colors">
                  <span className="text-sm text-white">{label}</span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={prefs?.[key] ?? true}
                      onChange={(e) => handleToggle(key, e.target.checked)}
                      className="peer sr-only"
                    />
                    <div className="w-10 h-6 bg-white/10 rounded-full peer-checked:bg-accent-green/30 transition-colors"></div>
                    <div className="absolute left-1 top-1 w-4 h-4 bg-white/50 rounded-full peer-checked:translate-x-4 peer-checked:bg-accent-green transition-all"></div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

// Fireflies Tab Component
function FirefliesTab() {
  const { data: status, isLoading } = useFirefliesStatus()
  const connectFireflies = useConnectFireflies()
  const disconnectFireflies = useDisconnectFireflies()
  const [apiKey, setApiKey] = useState('')
  const [error, setError] = useState('')

  const handleConnect = async () => {
    setError('')
    try {
      await connectFireflies.mutateAsync(apiKey)
      setApiKey('')
    } catch (err: any) {
      setError(err.message || 'Ошибка подключения')
    }
  }

  const handleDisconnect = () => {
    if (confirm('Отключить Fireflies? API ключ будет удалён.')) {
      disconnectFireflies.mutate()
    }
  }

  const webhookUrl = `${window.location.origin}/api/integrations/fireflies/webhook`

  return (
    <div className="space-y-6">
      <Card className="animate-in">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <svg className="w-5 h-5 text-accent-orange" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="23" />
              <line x1="8" y1="23" x2="16" y2="23" />
            </svg>
            Fireflies.ai
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {isLoading ? (
            <div className="text-center py-8 text-text-tertiary">Загрузка...</div>
          ) : status?.connected ? (
            <>
              {/* Connected state */}
              <div className="flex items-center gap-3 p-4 bg-accent-green/5 border border-accent-green/20 rounded-lg">
                <div className="w-3 h-3 rounded-full status-dot" />
                <div>
                  <p className="font-medium text-accent-green">Fireflies подключён</p>
                  <p className="text-xs text-text-tertiary mt-0.5">
                    {status.connectedAt && `Подключён ${new Date(status.connectedAt).toLocaleDateString('ru-RU')}`}
                  </p>
                </div>
              </div>

              {/* Masked key */}
              <div className="p-3 bg-white/5 border border-white/10 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-text-secondary">API ключ</span>
                  <span className="text-sm font-mono text-text-tertiary">••••••••••••••••</span>
                </div>
              </div>

              {/* Webhook URL */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">Webhook URL</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={webhookUrl}
                    readOnly
                    className="flex-1 px-3 py-2 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary text-sm font-mono"
                  />
                  <Button
                    variant="secondary"
                    onClick={() => navigator.clipboard.writeText(webhookUrl)}
                  >
                    Копировать
                  </Button>
                </div>
              </div>

              {/* Instructions */}
              <div className="p-4 bg-white/5 border border-white/10 rounded-lg">
                <h4 className="text-sm font-medium text-text-secondary mb-2">Настройка webhook</h4>
                <ol className="text-sm text-text-tertiary space-y-1.5 list-decimal list-inside">
                  <li>Войдите в <a href="https://app.fireflies.ai/integrations" target="_blank" rel="noopener noreferrer" className="text-accent-blue hover:underline">Fireflies Dashboard</a></li>
                  <li>Перейдите в Settings → Integrations → Webhooks</li>
                  <li>Добавьте URL выше как webhook endpoint</li>
                  <li>Выберите события: Transcription Complete</li>
                </ol>
              </div>

              {/* Disconnect */}
              <Button variant="danger" onClick={handleDisconnect} loading={disconnectFireflies.isPending}>
                Отключить Fireflies
              </Button>
            </>
          ) : (
            <>
              {/* Not connected state */}
              <div className="flex items-center gap-3 p-4 bg-white/5 border border-white/10 rounded-lg">
                <div className="w-3 h-3 rounded-full bg-text-tertiary" />
                <div>
                  <p className="font-medium text-text-secondary">Fireflies не подключён</p>
                  <p className="text-xs text-text-tertiary mt-0.5">Введите API ключ для подключения</p>
                </div>
              </div>

              {!status?.encryptionConfigured && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-400 text-sm">
                  Шифрование не настроено. Установите ENCRYPTION_KEY на сервере для безопасного хранения API ключей.
                </div>
              )}

              {/* API Key form */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">API ключ Fireflies</label>
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="Введите API ключ..."
                    className="flex-1 px-3 py-2 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary text-sm placeholder-text-tertiary focus:outline-none focus:border-accent-orange/50"
                  />
                  <Button
                    variant="primary"
                    onClick={handleConnect}
                    loading={connectFireflies.isPending}
                    disabled={!apiKey.trim()}
                  >
                    Подключить
                  </Button>
                </div>
                {error && <p className="text-xs text-red-400 mt-2">{error}</p>}
              </div>

              {/* Instructions */}
              <div className="p-4 bg-white/5 border border-white/10 rounded-lg">
                <h4 className="text-sm font-medium text-text-secondary mb-2">Как получить API ключ</h4>
                <ol className="text-sm text-text-tertiary space-y-1.5 list-decimal list-inside">
                  <li>Зарегистрируйтесь на <a href="https://fireflies.ai" target="_blank" rel="noopener noreferrer" className="text-accent-blue hover:underline">fireflies.ai</a></li>
                  <li>Перейдите в Settings → Developer</li>
                  <li>Сгенерируйте API ключ</li>
                  <li>Вставьте ключ в поле выше</li>
                </ol>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// AI Tab Component
function AiTab() {
  const { data: aiStatus, isLoading } = useAiStatus()
  const updateModel = useUpdateAiModel()

  return (
    <div className="space-y-6">
      <Card className="animate-in">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <svg className="w-5 h-5 text-violet-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 3l1.912 5.813a2 2 0 001.275 1.275L21 12l-5.813 1.912a2 2 0 00-1.275 1.275L12 21l-1.912-5.813a2 2 0 00-1.275-1.275L3 12l5.813-1.912a2 2 0 001.275-1.275L12 3z" />
            </svg>
            AI интеграция
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {isLoading ? (
            <div className="text-center py-8 text-text-tertiary">Загрузка...</div>
          ) : (
            <>
              {/* Status */}
              <div className="flex items-center gap-3 p-4 rounded-lg border" style={{
                backgroundColor: aiStatus?.configured ? 'rgba(132, 204, 22, 0.05)' : 'rgba(239, 68, 68, 0.05)',
                borderColor: aiStatus?.configured ? 'rgba(132, 204, 22, 0.2)' : 'rgba(239, 68, 68, 0.2)',
              }}>
                <div className={cn(
                  'w-3 h-3 rounded-full',
                  aiStatus?.configured ? 'status-dot' : 'bg-red-500'
                )} />
                <div>
                  <p className={cn('font-medium', aiStatus?.configured ? 'text-accent-lime' : 'text-red-400')}>
                    {aiStatus?.configured ? 'AI подключён' : 'AI не настроен'}
                  </p>
                  <p className="text-xs text-text-tertiary mt-0.5">
                    {aiStatus?.configured
                      ? `Используемая модель: ${aiStatus.model}`
                      : 'Установите ANTHROPIC_API_KEY на сервере'}
                  </p>
                </div>
              </div>

              {/* Available Models */}
              <div>
                <h4 className="text-sm font-medium text-text-secondary mb-3">Доступные модели</h4>
                <div className="space-y-2">
                  {aiStatus?.availableModels?.map((model) => (
                    <div
                      key={model.id}
                      onClick={() => updateModel.mutate(model.id)}
                      className={cn(
                        'flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer group',
                        aiStatus.model === model.id
                          ? 'bg-violet-500/10 border-violet-500/25'
                          : 'bg-white/5 border-white/10 hover:bg-white/10'
                      )}
                    >
                      <div>
                        <p className="text-sm font-medium text-white">{model.name}</p>
                        <p className="text-xs text-text-tertiary">{model.description}</p>
                      </div>
                      {aiStatus.model === model.id ? (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-violet-500/20 text-violet-400 border border-violet-500/30">
                          Активна
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-white/10 text-text-tertiary border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity">
                          Выбрать
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Usage Info */}
              <div className="p-4 bg-white/5 border border-white/10 rounded-lg">
                <h4 className="text-sm font-medium text-text-secondary mb-2">Возможности AI</h4>
                <ul className="text-sm text-text-tertiary space-y-1.5">
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-violet-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Генерация ТЗ из транскриптов встреч
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-violet-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Оценка трудозатрат по ТЗ
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-text-tertiary flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="16" />
                      <line x1="8" y1="12" x2="16" y2="12" />
                    </svg>
                    <span className="text-text-tertiary">Суммаризация транскриптов (скоро)</span>
                  </li>
                </ul>
              </div>

              <div className="text-xs text-text-tertiary">
                AI использует модели Claude от Anthropic. Стоимость зависит от выбранной модели и объёма текста.
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// Stages Tab Component
function StagesTab() {
  const { data: stages, isLoading } = useStages()
  const reorderStages = useReorderStages()

  const typeLabels: Record<string, string> = {
    lead: 'Лид',
    project: 'Проект',
    archive: 'Архив',
  }

  const typeColors: Record<string, string> = {
    lead: 'bg-accent-orange/20 text-accent-orange border-accent-orange/30',
    project: 'bg-accent-blue/20 text-accent-blue border-accent-blue/30',
    archive: 'bg-white/10 text-text-tertiary border-white/10',
  }

  const sortedStages = stages?.slice().sort((a, b) => a.position - b.position) || []

  const moveStage = (stageId: string, direction: 'up' | 'down') => {
    const idx = sortedStages.findIndex((s) => s.id === stageId)
    if (idx < 0) return
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1
    if (targetIdx < 0 || targetIdx >= sortedStages.length) return

    // Swap positions
    const newStages = sortedStages.map((s, i) => {
      if (i === idx) return { id: s.id, position: sortedStages[targetIdx].position }
      if (i === targetIdx) return { id: s.id, position: sortedStages[idx].position }
      return { id: s.id, position: s.position }
    })

    reorderStages.mutate(newStages)
  }

  return (
    <Card className="animate-in">
      <CardHeader>
        <CardTitle>Стадии проектов</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-text-tertiary mb-4">
          Управление стадиями Kanban-доски. Используйте стрелки для изменения порядка.
        </p>

        {isLoading ? (
          <div className="text-center py-8 text-text-tertiary">Загрузка...</div>
        ) : !sortedStages.length ? (
          <div className="text-center py-8 text-text-tertiary">Стадии не найдены</div>
        ) : (
          <div className="space-y-2">
            {sortedStages.map((stage, idx) => (
              <div
                key={stage.id}
                className="flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-lg hover:bg-white/8 transition-colors"
              >
                {/* Reorder buttons */}
                <div className="flex flex-col gap-0.5">
                  <button
                    onClick={() => moveStage(stage.id, 'up')}
                    disabled={idx === 0 || reorderStages.isPending}
                    className="p-0.5 text-text-tertiary hover:text-white disabled:opacity-20 transition-colors"
                    title="Вверх"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="18 15 12 9 6 15" />
                    </svg>
                  </button>
                  <button
                    onClick={() => moveStage(stage.id, 'down')}
                    disabled={idx === sortedStages.length - 1 || reorderStages.isPending}
                    className="p-0.5 text-text-tertiary hover:text-white disabled:opacity-20 transition-colors"
                    title="Вниз"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                </div>
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: stage.color }}
                />
                <span className="flex-1 text-white">{stage.name}</span>
                {stage.isFinal && (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-accent-green/20 text-accent-green border border-accent-green/30">
                    Финальная
                  </span>
                )}
                <span className={cn(
                  'px-2 py-0.5 text-xs rounded-full border',
                  typeColors[stage.type] || 'bg-white/10 text-text-tertiary border-white/10'
                )}>
                  {typeLabels[stage.type] || stage.type}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
